import React, { useState } from 'react';
import { Receipt, Edit3, Printer, Phone, FileMinus, Image as ImageIcon, X, StickyNote, Check } from 'lucide-react';
import { formatPrice, formatDate, compressImage, printRawHtml } from '../utils/utils.js';

const ReceiptModal = ({ order, storeSettings, onClose, onUpdateOrderDiscount, currentUser, products, onSaveNote }) => {
  const [isEditing, setIsEditing]         = useState(false);
  const [localSettings, setLocalSettings] = useState(storeSettings || {});
  const [editableDiscount, setEditableDiscount] = useState(order?.discount || 0);
  const [sharingReceipt, setSharingReceipt]     = useState(false);
  const [noteText, setNoteText]   = useState(order?.notes || '');
  const [noteSaved, setNoteSaved] = useState(false);
  const [editingNote, setEditingNote] = useState(false);

  if (!order) return null;

  const items    = order.items || [];
  const isDropShip = order.profile_id === 'profile3' || order.reseller_id != null;
  const storeName  = isDropShip ? (order.customerName || 'Store') : (localSettings.storeName || 'MY STORE');
  const currency   = localSettings.currency_symbol || 'Rs';
  const taxAuth    = localSettings.tax_authority || '';
  const taxRate    = parseFloat(localSettings.tax_rate || 0);
  const taxAmt     = parseFloat(order.tax_amount || 0);
  const discount   = isEditing ? parseFloat(editableDiscount || 0) : parseFloat(order.discount || 0);
  const total      = isEditing ? ((order.subtotal || 0) - parseFloat(editableDiscount || 0) + taxAmt) : parseFloat(order.total || 0);

  const handleSave = async () => {
    if (editableDiscount !== (order.discount || 0)) {
      const newTotal = (order.subtotal || 0) - parseFloat(editableDiscount) + taxAmt;
      await onUpdateOrderDiscount(order.id, parseFloat(editableDiscount), newTotal);
      order.discount = parseFloat(editableDiscount);
      order.total    = newTotal;
    }
    setIsEditing(false);
  };

  const handleLogoUpload = async (e) => {
    if (e.target.files[0]) {
      const base64 = await compressImage(e.target.files[0]);
      setLocalSettings(prev => ({ ...prev, logo: base64 }));
    }
  };

  // ── WhatsApp text share ───────────────────────────────────────────────────
  const handleWhatsApp = () => {
    let text = `*${storeName}*\n`;
    if (!isDropShip && localSettings.address) text += `${localSettings.address}\n`;
    if (!isDropShip && localSettings.contact)  text += `${localSettings.contact}\n`;
    text += `\nReceipt #${order.id.slice(0, 8).toUpperCase()}\n`;
    text += `Date: ${formatDate(order.createdAt)}\n`;
    text += `Customer: ${order.customerName || 'Walk-in'}\n`;
    if (order.staff_entered) text += `Cashier: ${order.staff_entered}\n`;
    text += `\n`;
    items.forEach(i => { if (i) text += `${i.qty}x ${i.name}${i.variant ? ' ('+i.variant+')' : ''} — ${currency} ${(i.price * i.qty).toLocaleString('en-PK')}\n`; });
    text += `\nSubtotal: ${formatPrice(order.subtotal)}`;
    if (discount > 0)  text += `\nDiscount: -${formatPrice(discount)}`;
    if (taxAmt > 0)    text += `\n${taxAuth ? taxAuth+' ' : ''}Tax${taxRate ? ' ('+taxRate+'%)' : ''}: ${formatPrice(taxAmt)}`;
    text += `\n*Total: ${formatPrice(total)}*`;
    text += `\nPayment: ${order.payment_method || 'Cash'}`;
    if ((order.loyaltyPointsEarned || 0) > 0) text += `\n⭐ Loyalty Earned: ${order.loyaltyPointsEarned} pts`;
    if (localSettings.receipt_footer) text += `\n\n${localSettings.receipt_footer}`;
    text += `\n\nThank you! 🙏`;
    const phone = (order.customerPhone || '').replace(/\D/g, '');
    const url   = `https://wa.me/${phone || ''}?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  };

  // ── Image share (html2canvas) ─────────────────────────────────────────────
  const handleSharePhoto = async () => {
    if (!window.html2canvas) { alert('Screenshot library not loaded.'); return; }
    setSharingReceipt(true);
    try {
      const el = document.getElementById('printable-receipt');
      const canvas = await window.html2canvas(el, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
      canvas.toBlob(async (blob) => {
        const file = new File([blob], 'receipt.png', { type: 'image/png' });
        if (navigator.canShare?.({ files: [file] })) {
          await navigator.share({ files: [file], title: 'Receipt', text: `Receipt from ${storeName}` });
        } else {
          const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'receipt.png'; a.click();
        }
        setSharingReceipt(false);
      }, 'image/png');
    } catch (e) {
      setSharingReceipt(false);
      alert('Could not capture: ' + e.message);
    }
  };

  // ── 80mm thermal print ────────────────────────────────────────────────────
  const handlePrint = () => {
    const logoHtml = (!isDropShip && localSettings.logo)
      ? `<img src="${localSettings.logo}" style="height:48px;object-fit:contain;display:block;margin:0 auto 6px"/>`
      : '';
    const itemsHtml = items.filter(Boolean).map(i => `
      <tr>
        <td style="padding:2px 0;font-size:11px">${i.qty}× ${i.name}${i.variant ? ' ('+i.variant+')' : ''}${i.isBox ? ' [BOX]' : ''}</td>
        <td style="padding:2px 0;font-size:11px;text-align:right;white-space:nowrap">${currency} ${(i.price * i.qty).toLocaleString('en-PK')}</td>
      </tr>`).join('');
    const taxLabel = taxAmt > 0
      ? `<tr><td style="font-size:11px">${taxAuth || 'Tax'}${taxRate ? ' ('+taxRate+'%)' : ''}</td><td style="text-align:right;font-size:11px">${currency} ${taxAmt.toLocaleString('en-PK')}</td></tr>`
      : '';

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
    <title>Receipt</title>
    <style>
      @page { size: 80mm auto; margin: 4mm; }
      * { box-sizing:border-box; margin:0; padding:0; }
      body { font-family:'Courier New',Courier,monospace; font-size:11px; color:#000; width:72mm; }
      .center { text-align:center; }
      .bold { font-weight:bold; }
      .big { font-size:14px; }
      .hr { border-top:1px dashed #000; margin:5px 0; }
      table { width:100%; border-collapse:collapse; }
      td { vertical-align:top; }
      .total-row td { font-weight:bold; font-size:13px; border-top:1px solid #000; padding-top:4px; margin-top:4px; }
    </style></head><body>
    <div class="center" style="margin-bottom:6px">
      ${logoHtml}
      <div class="bold big">${storeName}</div>
      ${!isDropShip && localSettings.address ? `<div>${localSettings.address}</div>` : ''}
      ${!isDropShip && localSettings.contact  ? `<div>${localSettings.contact}</div>` : ''}
    </div>
    <div class="hr"></div>
    <table style="margin-bottom:4px">
      <tr><td>Receipt #</td><td style="text-align:right">${order.id.slice(0,8).toUpperCase()}</td></tr>
      <tr><td>Date</td><td style="text-align:right">${formatDate(order.createdAt)}</td></tr>
      <tr><td>Customer</td><td style="text-align:right">${order.customerName || 'Walk-in'}</td></tr>
      ${order.staff_entered ? `<tr><td>Cashier</td><td style="text-align:right">${order.staff_entered}</td></tr>` : ''}
      <tr><td>Payment</td><td style="text-align:right">${order.payment_method || 'Cash'}</td></tr>
    </table>
    <div class="hr"></div>
    <table style="margin-bottom:6px">${itemsHtml}</table>
    <div class="hr"></div>
    <table>
      <tr><td style="font-size:11px">Subtotal</td><td style="text-align:right;font-size:11px">${currency} ${parseFloat(order.subtotal||0).toLocaleString('en-PK')}</td></tr>
      ${discount > 0 ? `<tr><td style="font-size:11px">Discount</td><td style="text-align:right;font-size:11px">- ${currency} ${discount.toLocaleString('en-PK')}</td></tr>` : ''}
      ${taxLabel}
      <tr class="total-row"><td>TOTAL</td><td style="text-align:right">${currency} ${total.toLocaleString('en-PK')}</td></tr>
    </table>
    ${(order.loyaltyPointsEarned||0) > 0 ? `<div class="hr"></div><div class="center" style="font-size:11px">⭐ Loyalty Earned: ${order.loyaltyPointsEarned} pts</div>` : ''}
    <div class="hr"></div>
    <div class="center" style="font-size:10px">
      ${localSettings.receipt_footer ? `<div style="margin-bottom:4px">${localSettings.receipt_footer}</div>` : ''}
      <div>Thank you for your visit!</div>
      ${localSettings.terms_conditions ? `<div style="margin-top:6px;font-size:9px">${localSettings.terms_conditions}</div>` : ''}
    </div>
    </body></html>`;
    printRawHtml(html, { lang: localStorage.getItem('pos_lang') || 'en' });
  };

  return (
    <div className="fixed inset-0 bg-[#1a1a1a]/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-sm rounded-xl shadow-lg border border-[#e1e3e5] overflow-hidden flex flex-col max-h-[95vh]">

        {/* Header */}
        <div className="p-3 bg-[#f6f7f8] border-b border-[#e1e3e5] flex justify-between items-center">
          <span className="font-bold text-[#1a1a1a] text-sm flex items-center gap-2"><Receipt size={16}/> Receipt</span>
          <div className="flex items-center gap-2">
            <button onClick={() => setIsEditing(e => !e)} className="text-xs bg-white border border-[#c9cccf] hover:bg-[#ebebeb] text-[#1a1a1a] px-2 py-1 rounded flex items-center gap-1">
              <Edit3 size={12}/> {isEditing ? 'Done' : 'Edit'}
            </button>
            <button onClick={onClose} className="text-[#5c5f62] hover:text-[#1a1a1a]"><X size={18}/></button>
          </div>
        </div>

        {/* Receipt preview */}
        <div className="overflow-y-auto flex-1 p-4 bg-[#f6f7f8]">
          <div className="bg-white rounded-lg border border-[#e1e3e5] shadow-sm p-5 font-mono text-xs" id="printable-receipt">

            {/* Store header */}
            {isEditing ? (
              <div className="space-y-2 mb-4 pb-4 border-b border-dashed border-[#c9cccf]">
                <label className="flex flex-col items-center cursor-pointer bg-[#f6f7f8] rounded p-2 border border-[#c9cccf]">
                  {localSettings.logo ? <img src={localSettings.logo} className="h-10 object-contain mb-1"/> : <ImageIcon size={18} className="text-[#5c5f62]"/>}
                  <span className="text-[10px] text-[#5c5f62]">Change Logo</span>
                  <input type="file" className="hidden" accept="image/*" onChange={handleLogoUpload}/>
                </label>
                <input value={localSettings.storeName||''} onChange={e => setLocalSettings({...localSettings, storeName: e.target.value})} placeholder="Store Name" className="w-full text-center font-bold border-b border-[#c9cccf] p-1 text-sm"/>
                <input value={localSettings.address||''} onChange={e => setLocalSettings({...localSettings, address: e.target.value})} placeholder="Address" className="w-full text-center border-b border-[#c9cccf] p-1"/>
                <input value={localSettings.contact||''} onChange={e => setLocalSettings({...localSettings, contact: e.target.value})} placeholder="Phone" className="w-full text-center border-b border-[#c9cccf] p-1"/>
                <input value={editableDiscount} onChange={e => setEditableDiscount(e.target.value)} type="number" placeholder="Discount" className="w-full text-center border border-[#c9cccf] p-1 rounded text-[#d82c0d] font-bold"/>
                <button onClick={handleSave} className="w-full bg-[#008060] text-white text-xs py-2 rounded-lg font-semibold">Save Changes</button>
              </div>
            ) : (
              <div className="text-center mb-3 pb-3 border-b border-dashed border-[#c9cccf]">
                {!isDropShip && localSettings.logo && <img src={localSettings.logo} alt="Logo" className="h-12 mx-auto mb-2 object-contain"/>}
                <div className="font-bold text-sm text-[#1a1a1a]">{storeName}</div>
                {!isDropShip && localSettings.address && <div className="text-[#5c5f62]">{localSettings.address}</div>}
                {!isDropShip && localSettings.contact  && <div className="text-[#5c5f62]">{localSettings.contact}</div>}
              </div>
            )}

            {/* Order meta */}
            <div className="mb-3 pb-3 border-b border-dashed border-[#c9cccf] space-y-0.5">
              <div className="flex justify-between"><span className="text-[#5c5f62]">Receipt #</span><span className="font-bold">{order.id.slice(0,8).toUpperCase()}</span></div>
              <div className="flex justify-between"><span className="text-[#5c5f62]">Date</span><span>{formatDate(order.createdAt)}</span></div>
              <div className="flex justify-between"><span className="text-[#5c5f62]">Customer</span><span>{order.customerName || 'Walk-in'}</span></div>
              {order.staff_entered && <div className="flex justify-between"><span className="text-[#5c5f62]">Cashier</span><span>{order.staff_entered}</span></div>}
              <div className="flex justify-between"><span className="text-[#5c5f62]">Payment</span><span className="font-semibold text-[#1a1a1a]">{order.payment_method || 'Cash'}</span></div>
              <div className="flex justify-between"><span className="text-[#5c5f62]">Status</span><span className={`font-semibold ${order.status === 'Credit' ? 'text-[#d82c0d]' : 'text-[#008060]'}`}>{order.status}</span></div>
            </div>

            {/* Items */}
            <div className="mb-3 pb-3 border-b border-dashed border-[#c9cccf] space-y-1">
              {items.filter(Boolean).map((item, idx) => (
                <div key={idx} className="flex justify-between gap-2">
                  <span className="text-[#1a1a1a]">{item.qty}× {item.name}{item.variant && <span className="text-[#5c5f62]"> ({item.variant})</span>}{item.isBox && <span className="text-[10px] bg-[#e4fcf4] text-[#008060] px-1 ml-1 rounded">BOX</span>}</span>
                  <span className="shrink-0">{formatPrice(item.price * item.qty)}</span>
                </div>
              ))}
            </div>

            {/* Totals */}
            <div className="space-y-1">
              <div className="flex justify-between text-[#5c5f62]"><span>Subtotal</span><span>{formatPrice(order.subtotal)}</span></div>
              {discount > 0 && <div className="flex justify-between text-[#d82c0d] font-semibold"><span>Discount</span><span>-{formatPrice(discount)}</span></div>}
              {taxAmt > 0 && (
                <div className="flex justify-between text-[#5c5f62]">
                  <span>{taxAuth || 'Tax'}{taxRate > 0 ? ` (${taxRate}%)` : ''}</span>
                  <span>{formatPrice(taxAmt)}</span>
                </div>
              )}
              <div className="flex justify-between font-bold text-sm text-[#1a1a1a] border-t border-[#c9cccf] pt-1 mt-1">
                <span>TOTAL</span>
                <span>{formatPrice(total)}</span>
              </div>
            </div>

            {/* Loyalty */}
            {(order.loyaltyPointsEarned || 0) > 0 && (
              <div className="mt-2 pt-2 border-t border-dashed border-[#c9cccf] text-center text-[10px] text-[#5c5f62]">
                ⭐ Loyalty Points Earned: <strong>{order.loyaltyPointsEarned}</strong>
              </div>
            )}

            {/* Staff Notes */}
            <div className="mt-3 pt-3 border-t border-dashed border-[#c9cccf]">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] font-bold text-[#5c5f62] flex items-center gap-1"><StickyNote size={10}/> Staff Note</span>
                <button onClick={() => setEditingNote(e => !e)} className="text-[10px] text-[#008060] font-semibold hover:underline">{editingNote ? 'Cancel' : 'Edit'}</button>
              </div>
              {editingNote ? (
                <div className="space-y-1">
                  <textarea value={noteText} onChange={e => setNoteText(e.target.value)} rows={2} placeholder="Internal note (not printed on receipt)…" className="w-full text-xs p-2 border border-[#c9cccf] rounded-lg outline-none resize-none focus:ring-1 focus:ring-[#008060]"/>
                  <button onClick={async () => {
                    if (onSaveNote) await onSaveNote(order.id, noteText);
                    order.notes = noteText;
                    setEditingNote(false);
                    setNoteSaved(true);
                    setTimeout(() => setNoteSaved(false), 2000);
                  }} className="w-full py-1.5 bg-[#008060] text-white text-xs rounded-lg font-semibold flex items-center justify-center gap-1">
                    <Check size={11}/> Save Note
                  </button>
                </div>
              ) : (
                <p className="text-[10px] text-[#5c5f62] italic min-h-[16px]">
                  {noteSaved ? '✓ Saved' : (noteText || '—')}
                </p>
              )}
            </div>

            {/* Footer */}
            <div className="mt-3 pt-3 border-t border-dashed border-[#c9cccf] text-center text-[10px] text-[#5c5f62] space-y-1">
              {localSettings.receipt_footer && <div>{localSettings.receipt_footer}</div>}
              <div>Thank you for your visit! 🙏</div>
              {localSettings.terms_conditions && (
                <div className="text-[9px] mt-1 whitespace-pre-line border-t border-dashed border-[#e1e3e5] pt-1">{localSettings.terms_conditions}</div>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="p-3 bg-[#f6f7f8] border-t border-[#e1e3e5] grid grid-cols-2 gap-2">
          <button onClick={handlePrint} className="py-2.5 bg-[#1a1a1a] text-white font-semibold rounded-lg flex items-center justify-center gap-2 hover:bg-black text-sm">
            <Printer size={15}/> Print 80mm
          </button>
          <button onClick={handleWhatsApp} className="py-2.5 bg-[#25D366] text-white font-semibold rounded-lg flex items-center justify-center gap-2 hover:bg-[#128C7E] text-sm">
            <Phone size={15}/> WhatsApp
          </button>
          <button onClick={handleSharePhoto} disabled={sharingReceipt} className="py-2.5 bg-[#008060] text-white font-semibold rounded-lg flex items-center justify-center gap-2 hover:bg-[#006e52] text-sm disabled:opacity-60 col-span-2">
            {sharingReceipt ? <><span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"/><span>Capturing…</span></> : <><ImageIcon size={15}/><span>Share as Image</span></>}
          </button>

          {/* Sold item photos */}
          {products && items.some(i => products.find(p => p?.id === i?.id)?.images?.[0]) && (
            <button onClick={async () => {
              const imgs = items.map(i => products?.find(p => p?.id === i?.id)?.images?.[0]).filter(Boolean);
              try {
                const blobs = await Promise.all(imgs.map(url => fetch(url).then(r => r.blob())));
                const files = blobs.map((b, i) => new File([b], `item_${i+1}.jpg`, { type: 'image/jpeg' }));
                if (navigator.canShare?.({ files })) {
                  await navigator.share({ files, title: 'Sold Items', text: `Order #${order.id.slice(0,6).toUpperCase()}` });
                } else { const a = document.createElement('a'); a.href = imgs[0]; a.download = 'item.jpg'; a.click(); }
              } catch (e) { alert('Could not share photos'); }
            }} className="col-span-2 py-2 bg-[#f6f7f8] border border-[#c9cccf] text-[#1a1a1a] font-semibold rounded-lg hover:bg-[#ebebeb] text-xs flex items-center justify-center gap-2">
              <ImageIcon size={13}/> Share Item Photos ({items.filter(i => products?.find(p => p?.id === i?.id)?.images?.[0]).length})
            </button>
          )}

          {/* Initiate return */}
          {order && !(order.status||'').includes('Returned') && (
            <button onClick={() => { onClose(); setTimeout(() => window._initiateReturnFromReceipt?.(order), 100); }}
              className="col-span-2 py-2 bg-[#fbeae5] text-[#d82c0d] border border-[#d82c0d]/30 font-semibold rounded-lg hover:bg-[#d82c0d] hover:text-white text-xs flex items-center justify-center gap-2 transition-colors">
              <FileMinus size={13}/> Initiate Return / Refund
            </button>
          )}
          <button onClick={onClose} className="col-span-2 py-2 bg-[#ebebeb] text-[#1a1a1a] font-semibold rounded-lg hover:bg-[#e4e5e7] text-sm">Close</button>
        </div>
      </div>
    </div>
  );
};

export default ReceiptModal;
